/**
 * API клієнт для doc.online_store_order (Порція Роздріб.4/5a).
 * Backend: /api/v1/retail/online-store-orders.
 *
 * Permissions:
 *  - retail.sales_channel.manage — POST/PATCH/DELETE/GET (заглушка фази 2)
 *  - retail.return.create — /status (FSM)
 */
import { api } from './client';
import type { Page } from './catalogs';

export type OnlineOrderStatus =
  | 'new'
  | 'confirmed'
  | 'preparing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'returned';

export interface OnlineStoreOrderItemIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  unit_id?: string | null;
  price_per_unit: string;
  discount_amount?: string;
}

export interface OnlineStoreOrderItem {
  id: string;
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  unit_id: string | null;
  price_per_unit: string;
  discount_amount: string | null;
  total: string | null;
}

export interface OnlineStoreOrderListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  sales_channel_id: string;
  customer_name: string | null;
  customer_phone: string | null;
  subtotal: string;
  total_amount: string;
  order_status: OnlineOrderStatus;
  fulfilled_retail_sale_id: string | null;
  posted: boolean;
  deletion_mark: boolean;
}

export interface OnlineStoreOrder extends OnlineStoreOrderListItem {
  external_order_id: string | null;
  external_order_status: string | null;
  customer_card_id: string | null;
  customer_contractor_id: string | null;
  customer_email: string | null;
  delivery_address: string | null;
  delivery_amount: string;
  discount_amount: string;
  posted_at: string | null;
  created_at: string;
  created_by: string | null;
  comment: string | null;
  items: OnlineStoreOrderItem[];
}

export interface OnlineStoreOrderCreate {
  organization_id: string;
  document_number?: string | null;
  document_date?: string | null;
  sales_channel_id: string;
  external_order_id?: string | null;
  external_order_status?: string | null;
  customer_card_id?: string | null;
  customer_contractor_id?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  delivery_address?: string | null;
  delivery_amount?: string;
  discount_amount?: string;
  items: OnlineStoreOrderItemIn[];
  comment?: string | null;
}

export interface OnlineStoreOrderUpdate {
  document_number?: string | null;
  document_date?: string | null;
  external_order_status?: string | null;
  order_status?: OnlineOrderStatus;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  delivery_address?: string | null;
  delivery_amount?: string;
  fulfilled_retail_sale_id?: string | null;
  comment?: string | null;
}

export const retailOnlineStoreOrders = {
  list: (params?: {
    organization_id?: string;
    sales_channel_id?: string;
    order_status?: OnlineOrderStatus;
    posted?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<OnlineStoreOrderListItem>>('/retail/online-store-orders', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<OnlineStoreOrder>(`/retail/online-store-orders/${id}`).then((r) => r.data),
  create: (payload: OnlineStoreOrderCreate) =>
    api.post<OnlineStoreOrder>('/retail/online-store-orders', payload).then((r) => r.data),
  update: (id: string, payload: OnlineStoreOrderUpdate) =>
    api.patch<OnlineStoreOrder>(`/retail/online-store-orders/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/retail/online-store-orders/${id}`).then(() => undefined),
  changeStatus: (id: string, new_status: OnlineOrderStatus) =>
    api.post<OnlineStoreOrder>(`/retail/online-store-orders/${id}/status`, { new_status })
      .then((r) => r.data),
};
